import { cn } from "@/lib/utils";
import { Check, User, MapPin, FileText } from "lucide-react";
import { motion } from "framer-motion";

interface CheckoutStepperProps {
  currentStep: number;
  steps: { label: string; icon: React.ElementType }[];
}

const CheckoutStepper = ({ currentStep, steps }: CheckoutStepperProps) => {
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => {
        const isComplete = index < currentStep;
        const isCurrent = index === currentStep;
        const Icon = step.icon;

        return (
          <div key={step.label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <motion.div
                initial={false}
                animate={{
                  scale: isCurrent ? 1.1 : 1,
                  backgroundColor: isComplete
                    ? "hsl(var(--primary))"
                    : isCurrent
                    ? "hsl(var(--primary) / 0.15)"
                    : "hsl(var(--muted))",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center transition-shadow duration-300",
                  isCurrent && "shadow-[0_0_20px_hsl(var(--primary)/0.3)] ring-2 ring-primary/30",
                  isComplete && "shadow-[0_0_15px_hsl(var(--primary)/0.2)]"
                )}
              >
                {isComplete ? (
                  <Check className="w-4 h-4 text-primary-foreground" />
                ) : (
                  <Icon
                    className={cn(
                      "w-4 h-4",
                      isCurrent ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                )}
              </motion.div>
              <span
                className={cn(
                  "text-[11px] font-body font-medium transition-colors",
                  isCurrent ? "text-primary font-semibold" : isComplete ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 mx-3 mt-[-20px]">
                <div className="h-[2px] bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={false}
                    animate={{ width: isComplete ? "100%" : "0%" }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="h-full bg-primary rounded-full"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const CHECKOUT_STEPS = [
  { label: "Details", icon: User },
  { label: "Shipping", icon: MapPin },
  { label: "Review", icon: FileText },
];

export default CheckoutStepper;
